"use strict";
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
const events_1 = require("events");
const entity_1 = require("./entity");
const decorator_1 = require("./decorator");
function CreateAssociation(association) {
    return association;
}
class BaseData extends events_1.EventEmitter {
    static get modelDefinition() {
        return {
            ...this.modelProperties,
            fields: (0, entity_1.getStaticEntityHelper)(this.Entity).fields,
        };
    }
    static get serverOnlyFieldNames() {
        return this.getFieldNamesByFilter(field => field.serverOnly);
    }
    static getFieldNamesByFilter(filter) {
        return this.modelDefinition.fields
            .filter(filter)
            .map(field => field.name);
    }
    static reportError(error) {
        if (BaseData.errorHandler) {
            BaseData.errorHandler(error);
        }
    }
    /**
     * Creates a new instance of the constructor
     *
     * @param {DataObject} data - The data object to initialize the instance with
     * @param {DataObject} _extraData - Additional data object
     * @param {Object} _options - Additional options for the constructor
     * @param {BaseData<any>} _options.parent - The parent object
     * @param {boolean} _options.fromBuilder - Indicates if the instance is created from a builder
     * @param {Object.<string, Function>} _options.listeners - Event listeners for the instance
     * @param {Cache} _options.cache - Cache object for the instance
     * @param {boolean} _options.disposable - Indicates if the instance is disposable
     */
    constructor(data, _extraData, _options, disposable = false) {
        super();
        /**
         *
         * @type {boolean}
         */
        this.privateDeleted = false;
        this.disposable = false;
        this.disposable = disposable;
        this.postCreate();
        this.setMonitoredEntity(data);
        if (!this.constructor[decorator_1.skipAddToCacheOnConstruct]) {
            this.setId();
        }
    }
    postCreate() {
    }
    /**
     * Abstract function to return private data.
     * Could  be overwritten by inherited classes
     *
     * @returns {{}}
     */
    get data() {
        return this.privateData;
    }
    get dataValues() {
        return this.data.getDataValues();
    }
    getData() {
        return this.data;
    }
    /**
     * Get flag indicating whether the object is deleted
     *
     * @returns {boolean}
     */
    get isDeleted() {
        return this.privateDeleted;
    }
    get isUpdated() {
        return (!this.isDeleted && this.data.isUpdated());
    }
    get isNew() {
        return (!this.isDeleted && this.data.isNew());
    }
    /**
     * Returns true if the current item is is a new data instance (does not have id field)
     *
     * @returns {boolean}
     */
    isNewItem() {
        return (this.data && typeof this.data.id === 'symbol');
    }
    /**
     * If there is no ID field on the data object then create an id symbol
     */
    setId() {
        this.data.suppressEvents();
        BaseData.createIdSymbol(this.data);
        this.data.unSuppressEvents();
    }
    static getModelName() {
        return this.modelDefinition.modelName;
    }
    static getTableName() {
        return this.modelDefinition.tableName;
    }
    /**
     * If there is no ID field on the passed in data object
     * then create an id symbol
     *
     * @param itemData
     */
    static createIdSymbol(itemData) {
        const allowedIdTypes = ['symbol', 'number'];
        if (!allowedIdTypes.includes(typeof itemData.id)) {
            itemData.id = Symbol(Math.random().toString(36).slice(-8));
        }
    }
    static get associationKey() {
        //
        // Unit tests may not have modelDefinition so use class name
        //
        const name = this.modelDefinition.tableName || this.name;
        return name.charAt(0).toLowerCase() + name.slice(1);
    }
    entityChanged(key, newValue, oldValue) {
    }
    /**
     * Create a private data entity holding the data of this object
     * and monitor changes on it
     *
     * @param {DataObject} data
     */
    setMonitoredEntity(data) {
        //
        // Create new entity to monitor change statuses
        //
        const EntityConstructor = this.constructor.Entity;
        if (!EntityConstructor || !EntityConstructor[entity_1.isEntitySymbol]) {
            throw new Error(`The configured entity for model '${this.constructor.name}' not yet decorated with @EntityDecorator`);
        }
        this.privateData = new EntityConstructor(data, this);
        if (this.disposable) {
            return;
        }
        //
        // Set up event forwarding
        //
        this.data.onFieldChanged = this.onEntityFieldChanged;
    }
    onEntityFieldChanged(key, newValue, oldValue) {
        this.entityChanged(key, newValue, oldValue);
        this.emit(BaseData.EVENTS.FIELD_CHANGED, {
            ...{
                key,
                oldValue,
                newValue,
            },
            ...{ item: this },
        });
    }
}
_a = decorator_1.skipAddToCacheOnConstruct;
/**
 * If you ever add new events here, make sure it is added to `NON_FORWARDING_EVENTS` or `EXTERNAL_EVENTS` if needed.
 */
BaseData.EVENTS = {
    DATA_LOADED: 'DATA_LOADED',
    MODEL_DATA_REQUEST: 'model_data_request',
    FUNCTION_CALL_REQUEST: 'function_call_request',
    MODEL_FULLTEXT_SEARCH: 'model_fulltext_search',
    NEW_ITEM_ADDED: 'newItemAdded',
    ITEM_DELETED: 'itemDeleted',
    FIELD_CHANGED: 'fieldChanged',
    SAVED: 'complexdata_saved',
    NEW_ITEM_ASSOCIATED: 'item_associated',
    ID_UPDATED: 'idUpdated',
    ASSOCIATION_CHANGED: 'associationChanged',
    MODEL_DATA_INITIALISED: 'model_data_initialised',
    OWN_ASSOCIATION_ID_UPDATED: 'own_association_id_updated',
    OWN_ASSOCIATION_ADDED: 'own_association_added',
    OWN_ASSOCIATION_SAVED: 'own_association_saved',
};
BaseData.NON_FORWARDING_EVENTS = [
    BaseData.EVENTS.OWN_ASSOCIATION_ID_UPDATED,
    BaseData.EVENTS.OWN_ASSOCIATION_ADDED,
    BaseData.EVENTS.OWN_ASSOCIATION_SAVED,
];
BaseData.FORWARDING_EVENTS = Object.values(BaseData.EVENTS).filter(eventName => !BaseData.NON_FORWARDING_EVENTS.includes(eventName));
BaseData.EXTERNAL_EVENTS = [
    BaseData.EVENTS.DATA_LOADED,
    BaseData.EVENTS.MODEL_DATA_REQUEST,
    BaseData.EVENTS.FUNCTION_CALL_REQUEST,
    BaseData.EVENTS.MODEL_DATA_INITIALISED,
    BaseData.EVENTS.MODEL_FULLTEXT_SEARCH,
];
BaseData[_a] = false;
exports.default = BaseData;
//# sourceMappingURL=../../src/baseData.js.map